import { useEffect, useState } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Shield, Users, Brain, Bot, Sparkles, History, Save, ChevronDown, ChevronUp, UserCheck, Loader2, Truck, Building2, GraduationCap, Target, Sparkle } from 'lucide-react';

// Compact horizontal status timeline: Booked → In-Transit → Delivered
const TIMELINE_STEPS = [
  { key: 'booked', label: 'Booked', match: ['booked', 'en_route_to_pickup', 'at_pickup'] },
  { key: 'transit', label: 'In-Transit', match: ['picked_up', 'in_transit', 'en_route_to_delivery'] },
  { key: 'delivered', label: 'Delivered', match: ['at_delivery', 'dropped_off', 'delivered', 'completed'] },
];
function LoadTimeline({ status }) {
  const s = (status || '').toLowerCase();
  const activeIdx = TIMELINE_STEPS.findIndex(step => step.match.includes(s));
  const current = activeIdx === -1 ? 0 : activeIdx;
  return (
    <div className="flex items-center gap-1" data-testid="load-timeline">
      {TIMELINE_STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={step.key} className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${done ? 'bg-emerald-500' : active ? 'bg-amber-500 ring-2 ring-amber-200' : 'bg-slate-300'}`} />
            <span className={`text-[10px] ${active ? 'text-amber-700 font-semibold' : done ? 'text-emerald-600' : 'text-slate-400'}`}>{step.label}</span>
            {i < TIMELINE_STEPS.length - 1 && <span className={`h-px w-4 ${done ? 'bg-emerald-400' : 'bg-slate-200'}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function Agents({ user }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [phase3Enabled, setPhase3Enabled] = useState(false);
  const [phase3Usage, setPhase3Usage] = useState(null); // {seat_cap, seats_used, seats_available}
  const [editingCap, setEditingCap] = useState(false);
  const [capDraft, setCapDraft] = useState('');
  const [savingCap, setSavingCap] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [holdersOpen, setHoldersOpen] = useState(false);
  const [holders, setHolders] = useState([]);
  const [holdersLoading, setHoldersLoading] = useState(false);
  // Bulk assign dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkEligible, setBulkEligible] = useState([]);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  // Training scorecards keyed by user id
  const [scorecards, setScorecards] = useState({});
  // Manage Drivers dialog (Phase 3 dispatcher roster)
  const [driversOpen, setDriversOpen] = useState(false);
  const [driversManager, setDriversManager] = useState(null); // agent obj being edited
  const [driversAvailable, setDriversAvailable] = useState([]);
  const [driversSelected, setDriversSelected] = useState(new Set());
  const [driversLoading, setDriversLoading] = useState(false);
  const [driversSaving, setDriversSaving] = useState(false);
  const MAX_DRIVERS_PER_P3 = 5;
  // Franchise Assignment dialog (super_admin assigns orgs to a broker_manager)
  const [franchiseOpen, setFranchiseOpen] = useState(false);
  const [franchiseTarget, setFranchiseTarget] = useState(null);
  const [franchiseOrgs, setFranchiseOrgs] = useState([]);
  const [franchiseSelected, setFranchiseSelected] = useState(new Set());
  const [franchiseSaving, setFranchiseSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    pin_code: ''
  });

  useEffect(() => {
    loadAgents();
    if (user?.role === 'super_admin') {
      setPhase3Enabled(true);
      loadUsage();
    } else if (user?.role === 'broker' || user?.role === 'broker_manager') {
      api.get('/org/autonomous-rules')
        .then(res => setPhase3Enabled(!!res.data.phase3_enabled))
        .catch(() => setPhase3Enabled(false));
      loadUsage();
    }
  }, [user?.role]);

  const loadUsage = () => {
    api.get('/org/phase3-usage')
      .then(res => setPhase3Usage(res.data))
      .catch(() => setPhase3Usage(null));
  };

  const openHistory = () => {
    const willOpen = !historyOpen;
    setHistoryOpen(willOpen);
    if (willOpen) {
      // Always re-fetch when opening — stale data risk otherwise
      setHistoryLoading(true);
      api.get('/ai-access-history')
        .then(res => setHistory(res.data.events || []))
        .catch(() => toast.error('Failed to load AI access history'))
        .finally(() => setHistoryLoading(false));
    }
  };

  const refreshHistoryIfOpen = () => {
    if (historyOpen) {
      api.get('/ai-access-history')
        .then(res => setHistory(res.data.events || []))
        .catch(() => {});
    }
  };

  const openBulkAssign = () => {
    setBulkOpen(true);
    setBulkResult(null);
    setBulkSelected(new Set());
    setBulkLoading(true);
    api.get('/phase3/eligible-users')
      .then(res => setBulkEligible(res.data.users || []))
      .catch(() => toast.error('Failed to load eligible users'))
      .finally(() => setBulkLoading(false));
  };

  const openHolders = () => {
    setHoldersOpen(true);
    setHoldersLoading(true);
    api.get('/phase3/holders')
      .then(res => setHolders(res.data.holders || []))
      .catch(() => toast.error('Failed to load Phase 3 holders'))
      .finally(() => setHoldersLoading(false));
  };

  const revokeHolder = async (h) => {
    if (!window.confirm(`Delete Phase 3 AI access from "${h.name}"? Their seat will be freed immediately.`)) return;
    try {
      await api.post(`/users/${h.id}/toggle-ai-mode?mode=semi_autonomous`);
      toast.success(`Phase 3 deleted for ${h.name}`);
      setHolders(prev => prev.filter(x => x.id !== h.id));
      loadAgents();
      loadUsage();
      refreshHistoryIfOpen();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete Phase 3');
    }
  };

  const exportHoldersCSV = () => {
    if (!holders.length) {
      toast.error('No holders to export');
      return;
    }
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ['Name', 'Email', 'Role', 'Granted By', 'Granted By Role', 'Granted At'];
    const rows = holders.map(h => [
      h.name, h.email, h.role, h.granted_by || '', h.granted_by_role || '',
      h.granted_at ? new Date(h.granted_at).toISOString() : '',
    ].map(esc).join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `phase3-holders-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${holders.length} holder(s) to CSV`);
  };

  const toggleBulkSelect = (id) => {
    const next = new Set(bulkSelected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setBulkSelected(next);
  };

  const submitBulkAssign = async () => {
    if (bulkSelected.size === 0) {
      toast.error('Select at least one user');
      return;
    }
    setBulkSubmitting(true);
    try {
      const res = await api.post('/phase3/bulk-assign', { user_ids: Array.from(bulkSelected) });
      setBulkResult(res.data);
      toast.success(`Granted Phase 3 to ${res.data.granted_count} user(s)`);
      loadAgents();
      loadUsage();
      refreshHistoryIfOpen();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const saveSeatCap = async () => {
    if (!user?.organization_id && user?.role !== 'super_admin') return;
    const orgId = user.role === 'super_admin' ? null : user.organization_id;
    if (!orgId) {
      toast.error('Super admin: use Super Admin panel to set cap per franchise');
      setEditingCap(false);
      return;
    }
    setSavingCap(true);
    try {
      const raw = capDraft.trim();
      const payload = { seat_cap: raw === '' ? null : Number(raw) };
      await api.put(`/admin/organizations/${orgId}/phase3-seat-cap`, payload);
      toast.success('Seat cap updated');
      setEditingCap(false);
      loadUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update cap');
    } finally {
      setSavingCap(false);
    }
  };

  const loadAgents = () => {
    api.get('/agents')
      .then(async res => {
        const list = res.data;
        setAgents(list);
        // Fetch scorecards for every user in training mode (or already graduated)
        const trainees = list.filter(a => a.training_mode || a.training_graduated_at);
        const cards = {};
        await Promise.all(trainees.map(async (a) => {
          try {
            const r = await api.get(`/training/scorecard/${a.id}`);
            cards[a.id] = r.data;
          } catch (_) { /* skip */ }
        }));
        setScorecards(cards);
      })
      .catch(() => toast.error('Failed to load agents'))
      .finally(() => setLoading(false));
  };

  const seedScenarios = async (agent) => {
    if (!window.confirm(`Load 3 practice scenarios (easy/medium/tough) for ${agent.name}? They'll appear on their AI Dispatch page.`)) return;
    try {
      const res = await api.post(`/training/scenarios/seed?user_id=${agent.id}`);
      toast.success(`Loaded ${res.data.count} practice scenarios for ${agent.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed scenarios');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate PIN is 4 digits
    if (formData.pin_code.length !== 4) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }
    
    api.post('/agents', formData)
      .then(() => {
        toast.success('Agent created successfully');
        setShowForm(false);
        setFormData({ name: '', email: '', pin_code: '' });
        loadAgents();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to create agent'));
  };

  const promoteAgent = (agentId, promoteTo) => {
    const roleText = promoteTo === 'broker_manager' ? 'Broker Manager' : 'Broker';
    if (!window.confirm(`Are you sure you want to promote this agent to ${roleText}? They will have expanded access.`)) {
      return;
    }

    api.post(`/agents/promote/${agentId}?promote_to=${promoteTo}`)
      .then(() => {
        toast.success(`Agent promoted to ${roleText} successfully`);
        loadAgents();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to promote agent'));
  };

  const changeUserRole = (userId, userName, currentRole, newRole) => {
    const roleNames = {
      'agent': 'Agent',
      'broker': 'Broker',
      'broker_manager': 'Broker Manager'
    };
    
    if (!window.confirm(`Change ${userName}'s role from ${roleNames[currentRole]} to ${roleNames[newRole]}?`)) {
      return;
    }

    api.post(`/users/${userId}/change-role?new_role=${newRole}`)
      .then(() => {
        toast.success(`Role changed to ${roleNames[newRole]} successfully`);
        loadAgents();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to change role'));
  };

  const deleteAgent = (agentId, agentName) => {
    if (!window.confirm(`Are you sure you want to delete ${agentName}? This action cannot be undone.`)) {
      return;
    }
    api.delete(`/users/${agentId}`)
      .then(res => {
        if (res.data?.freed_phase3_seat) {
          toast.success('User deleted — Phase 3 seat freed');
        } else {
          toast.success('User deleted successfully');
        }
        loadAgents();
        loadUsage();
        refreshHistoryIfOpen();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to delete'));
  };

  const toggleDeletePermission = (userId, userName) => {
    api.post(`/users/${userId}/toggle-delete-permission`)
      .then(res => {
        toast.success(res.data.message);
        loadAgents();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to toggle permission'));
  };

  const toggleAiMode = (userId, userName) => {
    api.post(`/users/${userId}/toggle-ai-mode`)
      .then(res => {
        toast.success(res.data.message);
        loadAgents();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to change AI mode'));
  };

  const assignPhase3 = async (agent) => {
    // Auto-promote agent → broker_manager if needed
    if (agent.role === 'agent') {
      const ok = window.confirm(
        `Phase 3 AI is for Managers, Brokers, and Master Admins only.\n\n` +
        `Promote "${agent.name}" from Agent to Broker Manager first, then assign Phase 3?`
      );
      if (!ok) return;
      try {
        await api.post(`/users/${agent.id}/change-role?new_role=broker_manager`);
        toast.success(`${agent.name} promoted to Broker Manager`);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Promotion failed');
        return;
      }
    } else {
      const ok = window.confirm(
        `Assign Phase 3 Autonomous AI to "${agent.name}"?\n\n` +
        `They will be able to auto-book loads with no manual approval.`
      );
      if (!ok) return;
    }

    try {
      await api.post(`/users/${agent.id}/toggle-ai-mode?mode=autonomous`);
      toast.success(`Phase 3 AI assigned to ${agent.name}`);
      loadAgents();
      loadUsage();
      refreshHistoryIfOpen();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign Phase 3');
      loadAgents();
      loadUsage();
    }
  };

  const revokePhase3 = async (agent) => {
    if (!window.confirm(`Delete Phase 3 AI access from "${agent.name}"? They'll drop to Semi-Auto and their seat will be freed.`)) return;
    try {
      await api.post(`/users/${agent.id}/toggle-ai-mode?mode=semi_autonomous`);
      toast.success(`Phase 3 deleted for ${agent.name}`);
      loadAgents();
      loadUsage();
      refreshHistoryIfOpen();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete Phase 3');
    }
  };

  const openManageDrivers = async (agent) => {
    setDriversManager(agent);
    setDriversOpen(true);
    setDriversLoading(true);
    setDriversSelected(new Set());
    setDriversAvailable([]);
    try {
      const [availRes, assignedRes] = await Promise.all([
        api.get(`/users/${agent.id}/available-drivers`),
        api.get(`/users/${agent.id}/assigned-drivers`),
      ]);
      const assignedDrivers = assignedRes.data.drivers || [];
      const loadMap = new Map(assignedDrivers.map(d => [d.id, d.active_load]));
      // Merge active_load onto the picker list so rows can show load info
      const enriched = (availRes.data.drivers || []).map(d => ({
        ...d,
        active_load: loadMap.get(d.id) || null,
      }));
      setDriversAvailable(enriched);
      setDriversSelected(new Set(assignedDrivers.map(d => d.id)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load drivers');
    } finally {
      setDriversLoading(false);
    }
  };

  const toggleDriverSelect = (driverId) => {
    const next = new Set(driversSelected);
    if (next.has(driverId)) {
      next.delete(driverId);
    } else {
      if (next.size >= MAX_DRIVERS_PER_P3) {
        toast.error(`Max ${MAX_DRIVERS_PER_P3} drivers per Phase 3 dispatcher`);
        return;
      }
      next.add(driverId);
    }
    setDriversSelected(next);
  };

  const saveDriverAssignments = async () => {
    if (!driversManager) return;
    setDriversSaving(true);
    try {
      const res = await api.post(`/users/${driversManager.id}/assign-drivers`, {
        driver_ids: Array.from(driversSelected),
      });
      toast.success(`Assigned ${res.data.assigned_count} driver(s) to ${driversManager.name}`);
      setDriversOpen(false);
      loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save assignments');
    } finally {
      setDriversSaving(false);
    }
  };

  const openManageFranchises = async (agent) => {
    setFranchiseTarget(agent);
    setFranchiseOpen(true);
    setFranchiseSelected(new Set(agent.assigned_organizations || []));
    try {
      const res = await api.get('/organizations');
      setFranchiseOrgs(res.data || []);
    } catch (err) {
      toast.error('Failed to load franchises');
    }
  };

  const toggleFranchiseSelect = (orgId) => {
    const next = new Set(franchiseSelected);
    next.has(orgId) ? next.delete(orgId) : next.add(orgId);
    setFranchiseSelected(next);
  };

  const saveFranchiseAssignments = async () => {
    if (!franchiseTarget) return;
    setFranchiseSaving(true);
    try {
      const res = await api.put(`/users/${franchiseTarget.id}/assigned-organizations`, {
        organization_ids: Array.from(franchiseSelected),
      });
      toast.success(`${franchiseTarget.name} now oversees ${res.data.assigned_organizations.length} franchise(s)`);
      setFranchiseOpen(false);
      loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setFranchiseSaving(false);
    }
  };

  const toggleTrainingMode = async (agent) => {
    const nextState = !agent.training_mode;
    if (!window.confirm(
      nextState
        ? `Turn Training Mode ON for ${agent.name}? Their emails and bookings won't hit the real world.`
        : `Turn Training Mode OFF for ${agent.name}? Their next actions will go live.`
    )) return;
    try {
      const res = await api.put(`/users/${agent.id}/toggle-training-mode`);
      toast.success(`${agent.name} training mode → ${res.data.training_mode ? 'ON' : 'OFF'}`);
      loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle');
    }
  };

  if (user.role !== 'broker' && user.role !== 'broker_manager' && user.role !== 'super_admin') {
    return (
      <div className="text-center py-12" data-testid="agents-page">
        <Shield className="w-16 h-16 mx-auto text-slate-400 mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Required</h2>
        <p className="text-slate-600">Broker or Manager access required to manage users.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="p-8">Loading agents...</div>;
  }

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{user.role === 'super_admin' ? 'User Management' : 'Agent Management'}</h1>
          <p className="text-slate-600 mt-1">{user.role === 'super_admin' ? 'View and manage all users across franchises' : 'View and manage your agents, promote them to broker role'}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
            <p className="text-sm text-blue-700 font-medium">Total Users: {agents.length}</p>
          </div>
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild>
              <Button data-testid="add-agent-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Agent
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Agent</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    required
                    placeholder="Agent name"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    required
                    type="email"
                    placeholder="agent@example.com"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>4-Digit PIN *</Label>
                  <Input
                    required
                    type="text"
                    maxLength={4}
                    placeholder="1234"
                    value={formData.pin_code}
                    onChange={e => setFormData({...formData, pin_code: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                  />
                  <p className="text-xs text-slate-500 mt-1">Agent will use this PIN to login</p>
                </div>
                <Button type="submit" className="w-full">Create Agent</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Phase 3 Seat Cap + AI Access History */}
      {(user.role === 'super_admin' || user.role === 'broker' || user.role === 'broker_manager') && phase3Usage && (
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="phase3-seat-panel">
          <CardContent className="py-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-amber-500/20 p-2 rounded-lg">
                  <Bot className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    Phase 3 AI Seats
                    {phase3Usage.enabled ? (
                      <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full">ACTIVE</span>
                    ) : (
                      <span className="bg-slate-200 text-slate-600 text-[10px] font-bold px-2 py-0.5 rounded-full">LOCKED</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Managers holding autonomous AI in this franchise.
                  </p>
                </div>
              </div>

              <div className="flex-1 flex flex-wrap items-center gap-4">
                <div className="text-center px-3 py-1.5 bg-white rounded-lg border border-amber-200" data-testid="seats-used">
                  <p className="text-2xl font-bold text-amber-700">{phase3Usage.seats_used ?? 0}</p>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Used</p>
                </div>
                <div className="text-center px-3 py-1.5 bg-white rounded-lg border border-slate-200">
                  <p className="text-2xl font-bold text-slate-700">
                    {phase3Usage.seat_cap != null ? phase3Usage.seat_cap : '∞'}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Cap</p>
                </div>
                {phase3Usage.seat_cap != null && (
                  <div className="text-center px-3 py-1.5 bg-white rounded-lg border border-green-200">
                    <p className={`text-2xl font-bold ${(phase3Usage.seats_available ?? 0) <= 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {phase3Usage.seats_available ?? 0}
                    </p>
                    <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Available</p>
                  </div>
                )}
              </div>

              {/* Cap editor — franchise broker for their own org */}
              {user.role === 'broker' && phase3Usage.enabled && (
                editingCap ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={1}
                      placeholder="unlimited"
                      value={capDraft}
                      onChange={(e) => setCapDraft(e.target.value)}
                      className="w-28 text-sm"
                      data-testid="seat-cap-input"
                    />
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={saveSeatCap}
                      disabled={savingCap}
                      data-testid="save-seat-cap-btn"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingCap(false)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => {
                      setCapDraft(phase3Usage.seat_cap != null ? String(phase3Usage.seat_cap) : '');
                      setEditingCap(true);
                    }}
                    data-testid="edit-seat-cap-btn"
                  >
                    Edit Cap
                  </Button>
                )
              )}

              {/* Super admin — link to Super Admin panel where per-franchise cap can be set */}
              {user.role === 'super_admin' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.location.assign('/super-admin')}
                  data-testid="super-admin-cap-link"
                >
                  Set Per-Franchise Cap →
                </Button>
              )}

              <Button
                size="sm"
                variant="outline"
                onClick={openHolders}
                data-testid="view-phase3-holders-btn"
                className="border-amber-400 text-amber-700 hover:bg-amber-50"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                View Phase 3 Holders
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={openBulkAssign}
                data-testid="bulk-assign-phase3-btn"
              >
                <UserCheck className="w-4 h-4 mr-1.5" />
                Bulk Assign
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={openHistory}
                data-testid="toggle-ai-history-btn"
              >
                <History className="w-4 h-4 mr-1.5" />
                AI Access History
                {historyOpen ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
              </Button>
            </div>

            {/* History panel */}
            {historyOpen && (
              <div className="mt-4 pt-4 border-t border-amber-200" data-testid="ai-history-panel">
                {historyLoading ? (
                  <p className="text-sm text-slate-500 py-6 text-center">Loading history...</p>
                ) : history.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">No AI access changes recorded yet.</p>
                ) : (
                  <div className="max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white/60 sticky top-0">
                        <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                          <th className="py-2 px-3">When</th>
                          <th className="py-2 px-3">Granted By</th>
                          <th className="py-2 px-3">Action</th>
                          <th className="py-2 px-3">Target User</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((e, i) => {
                          const isPhase3 = e.action === 'ai_mode_autonomous';
                          const isRevoke = e.action === 'ai_mode_semi_autonomous' || e.action === 'ai_mode_assistant';
                          return (
                            <tr key={i} className="border-t border-amber-100 hover:bg-white/40">
                              <td className="py-2 px-3 text-xs text-slate-600 whitespace-nowrap">
                                {e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}
                              </td>
                              <td className="py-2 px-3">
                                <span className="font-medium text-slate-800">{e.user_name || '—'}</span>
                                <span className="text-[10px] text-slate-500 ml-1">({e.user_role})</span>
                              </td>
                              <td className="py-2 px-3">
                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${
                                  isPhase3 ? 'bg-amber-100 text-amber-800' : isRevoke ? 'bg-slate-200 text-slate-700' : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {isPhase3 ? 'GRANTED PHASE 3' : isRevoke ? 'DELETED PHASE 3' : e.action.toUpperCase()}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-slate-700">{e.resource_name || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <UserPlus className="w-5 h-5 mr-2" />
            How to Add New Agents
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-700">
          <p><strong>Step 1:</strong> Share the platform URL with your agent: <code className="bg-white px-2 py-1 rounded">https://dispatch-portal-9.preview.emergentagent.com</code></p>
          <p><strong>Step 2:</strong> Tell them to leave the master code field blank and create a 4-digit PIN</p>
          <p><strong>Step 3:</strong> They will be created as an "Agent" with limited access (can book loads, manage carriers)</p>
          <p><strong>Step 4:</strong> You can promote them to "Broker" from this page to give them full access</p>
        </CardContent>
      </Card>

      {agents.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Users className="w-16 h-16 mx-auto text-slate-400 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Agents Yet</h3>
            <p className="text-slate-600">Share the login link with your team to add agents.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map(agent => (
            <Card key={agent.id} className="card-hover" data-testid={`agent-card-${agent.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{agent.name}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">{agent.email}</p>
                    {agent.pin_code && (
                      <p className="text-sm font-bold text-blue-600 mt-1">Agent ID: {agent.pin_code}</p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    agent.role === 'broker' 
                      ? 'bg-green-100 text-green-700' 
                      : agent.role === 'broker_manager'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-slate-100 text-slate-700'
                  }`}>
                    {agent.role === 'broker' ? '👑 Broker' : agent.role === 'broker_manager' ? '⭐ Manager' : '👤 Agent'}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-slate-600">
                  {user.role === 'super_admin' && agent.organization_name && (
                    <p><strong>Franchise:</strong> {agent.organization_name}</p>
                  )}
                  <p><strong>Joined:</strong> {new Date(agent.created_at).toLocaleDateString()}</p>
                </div>
                
                {/* Super Admin - Full Role Management */}
                {user.role === 'super_admin' && (
                  <div>
                    <p className="text-xs text-slate-600 mb-2 font-medium">Change Role:</p>
                    <div className="grid grid-cols-3 gap-2">
                      <Button 
                        size="sm" 
                        variant={agent.role === 'agent' ? 'default' : 'outline'}
                        className={agent.role === 'agent' ? 'bg-slate-600' : ''}
                        onClick={() => agent.role !== 'agent' && changeUserRole(agent.id, agent.name, agent.role, 'agent')}
                        disabled={agent.role === 'agent'}
                        data-testid={`role-agent-${agent.id}`}
                      >
                        👤 Agent
                      </Button>
                      <Button 
                        size="sm" 
                        variant={agent.role === 'broker' ? 'default' : 'outline'}
                        className={agent.role === 'broker' ? 'bg-green-600' : ''}
                        onClick={() => agent.role !== 'broker' && changeUserRole(agent.id, agent.name, agent.role, 'broker')}
                        disabled={agent.role === 'broker'}
                        data-testid={`role-broker-${agent.id}`}
                      >
                        👑 Broker
                      </Button>
                      <Button 
                        size="sm" 
                        variant={agent.role === 'broker_manager' ? 'default' : 'outline'}
                        className={agent.role === 'broker_manager' ? 'bg-blue-600' : ''}
                        onClick={() => agent.role !== 'broker_manager' && changeUserRole(agent.id, agent.name, agent.role, 'broker_manager')}
                        disabled={agent.role === 'broker_manager'}
                        data-testid={`role-manager-${agent.id}`}
                      >
                        ⭐ Manager
                      </Button>
                    </div>
                  </div>
                )}

                {/* Regular Broker - Can promote and manage roles */}
                {user.role === 'broker' && (
                  <div>
                    <p className="text-xs text-slate-600 mb-2 font-medium">Change Role:</p>
                    <div className="grid grid-cols-3 gap-2">
                      <Button 
                        size="sm" 
                        variant={agent.role === 'agent' ? 'default' : 'outline'}
                        className={agent.role === 'agent' ? 'bg-slate-600' : ''}
                        onClick={() => agent.role !== 'agent' && changeUserRole(agent.id, agent.name, agent.role, 'agent')}
                        disabled={agent.role === 'agent'}
                        data-testid={`role-agent-b-${agent.id}`}
                      >
                        Agent
                      </Button>
                      <Button 
                        size="sm" 
                        variant={agent.role === 'broker' ? 'default' : 'outline'}
                        className={agent.role === 'broker' ? 'bg-green-600' : ''}
                        onClick={() => agent.role !== 'broker' && changeUserRole(agent.id, agent.name, agent.role, 'broker')}
                        disabled={agent.role === 'broker'}
                        data-testid={`role-broker-b-${agent.id}`}
                      >
                        Broker
                      </Button>
                      <Button 
                        size="sm" 
                        variant={agent.role === 'broker_manager' ? 'default' : 'outline'}
                        className={agent.role === 'broker_manager' ? 'bg-blue-600' : ''}
                        onClick={() => agent.role !== 'broker_manager' && changeUserRole(agent.id, agent.name, agent.role, 'broker_manager')}
                        disabled={agent.role === 'broker_manager'}
                        data-testid={`role-manager-b-${agent.id}`}
                      >
                        Manager
                      </Button>
                    </div>
                  </div>
                )}

                {/* Delete permission toggle - only for franchise owner (can_delete_users) */}
                {user.can_delete_users && agent.id !== user.id && (agent.role === 'broker' || agent.role === 'broker_manager') && (
                  <Button
                    size="sm"
                    variant="outline"
                    className={`w-full mt-2 text-xs ${agent.can_delete_users ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                    onClick={() => toggleDeletePermission(agent.id, agent.name)}
                    data-testid={`toggle-delete-perm-${agent.id}`}
                  >
                    <Shield className="w-3 h-3 mr-1" />
                    {agent.can_delete_users ? 'Revoke Delete Access' : 'Grant Delete Access'}
                  </Button>
                )}

                {/* AI Mode toggle - only super admin or franchise broker (owner) */}
                {(user.role === 'super_admin' || user.role === 'broker') && agent.id !== user.id && agent.role === 'agent' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className={`w-full mt-2 text-xs ${agent.ai_mode === 'semi_autonomous' ? 'border-purple-400 bg-purple-50 text-purple-700 hover:bg-purple-100' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                    onClick={() => toggleAiMode(agent.id, agent.name)}
                    data-testid={`toggle-ai-mode-${agent.id}`}
                  >
                    <Brain className="w-3 h-3 mr-1" />
                    {agent.ai_mode === 'semi_autonomous' ? 'Downgrade to AI Assistant' : 'Upgrade to AI Semi-Auto'}
                  </Button>
                )}

                {/* Phase 3 AI grant/delete — Master Admin or Franchise Broker only. */}
                {(user.role === 'super_admin' || (user.role === 'broker' && phase3Enabled)) && agent.id !== user.id && (
                  agent.ai_mode === 'autonomous' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full mt-2 text-xs border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
                      onClick={() => revokePhase3(agent)}
                      data-testid={`revoke-phase3-${agent.id}`}
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      Phase 3 Active · Delete
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="w-full mt-2 text-xs bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                      onClick={() => assignPhase3(agent)}
                      data-testid={`assign-phase3-${agent.id}`}
                    >
                      <Bot className="w-3 h-3 mr-1" />
                      {agent.role === 'agent' ? 'Promote + Assign Phase 3 AI' : 'Assign Phase 3 AI'}
                    </Button>
                  )
                )}

                {/* Manage Drivers — Master Admin, Franchise Broker, OR a Phase 3 broker_manager. */}
                {agent.ai_mode === 'autonomous' && (agent.role === 'broker_manager' || agent.role === 'broker') && (
                  user.role === 'super_admin' || user.role === 'broker' ||
                  (user.role === 'broker_manager' && user.ai_mode === 'autonomous')
                ) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full mt-2 text-xs border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    onClick={() => openManageDrivers(agent)}
                    data-testid={`manage-drivers-${agent.id}`}
                  >
                    <Truck className="w-3 h-3 mr-1" />
                    Manage Drivers (max {MAX_DRIVERS_PER_P3})
                  </Button>
                )}

                {/* Manage Franchises — Master Admin can assign specific franchises to any broker_manager */}
                {user.role === 'super_admin' && agent.role === 'broker_manager' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full mt-2 text-xs border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                    onClick={() => openManageFranchises(agent)}
                    data-testid={`manage-franchises-${agent.id}`}
                  >
                    <Building2 className="w-3 h-3 mr-1" />
                    Manage Franchises ({(agent.assigned_organizations || []).length})
                  </Button>
                )}

                {/* Training Mode — Master Admin or Franchise Broker can flip any user (not self). */}
                {(user.role === 'super_admin' || user.role === 'broker') && agent.id !== user.id && (
                  <Button
                    size="sm"
                    variant="outline"
                    className={`w-full mt-2 text-xs ${
                      agent.training_mode
                        ? 'border-amber-400 bg-amber-100 text-amber-800 hover:bg-amber-200'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                    onClick={() => toggleTrainingMode(agent)}
                    data-testid={`toggle-training-${agent.id}`}
                  >
                    <Brain className="w-3 h-3 mr-1" />
                    Training Mode: {agent.training_mode ? 'ON' : 'OFF'}
                  </Button>
                )}

                {/* Graduated badge — shown to admins if the trainee auto-graduated */}
                {agent.training_graduated_at && !agent.training_mode && (
                  <div
                    className="w-full mt-2 text-[11px] bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded px-2 py-1.5 flex items-center gap-1.5 text-emerald-800 font-semibold"
                    data-testid={`graduated-badge-${agent.id}`}
                  >
                    <GraduationCap className="w-3.5 h-3.5" />
                    Graduated · {agent.training_graduation_score || scorecards[agent.id]?.successful_count || '20'} successful actions
                  </div>
                )}

                {/* Scorecard mini-panel — visible when trainee is in training mode */}
                {agent.training_mode && scorecards[agent.id] && (
                  <div
                    className="w-full mt-2 bg-amber-50 border border-amber-200 rounded px-2 py-2 space-y-1.5"
                    data-testid={`scorecard-${agent.id}`}
                  >
                    <div className="flex items-center justify-between text-[11px] font-semibold text-amber-900">
                      <span className="flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        Practice: {scorecards[agent.id].successful_count} / {scorecards[agent.id].graduation_threshold}
                      </span>
                      <span className="text-amber-700">{scorecards[agent.id].accuracy}% acc</span>
                    </div>
                    <div className="w-full h-1.5 bg-amber-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all"
                        style={{ width: `${scorecards[agent.id].progress_pct}%` }}
                        data-testid={`scorecard-progress-${agent.id}`}
                      />
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-amber-800/80">
                      <span>Inq {scorecards[agent.id].counts.inquiry_sent}</span>
                      <span>·</span>
                      <span>Book {scorecards[agent.id].counts.auto_book}</span>
                      <span>·</span>
                      <span>Fac {scorecards[agent.id].counts.factoring_email}</span>
                      <span>·</span>
                      <span className="opacity-70">Drafts {scorecards[agent.id].counts.ai_draft_generated}</span>
                    </div>
                    {(user.role === 'super_admin' || user.role === 'broker') && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full text-[11px] mt-1 border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 h-7"
                        onClick={() => seedScenarios(agent)}
                        data-testid={`seed-scenarios-${agent.id}`}
                      >
                        <Sparkle className="w-3 h-3 mr-1" />
                        Load 3 Practice Scenarios
                      </Button>
                    )}
                  </div>
                )}

                {/* Info note when broker but no phase3_enabled */}
                {user.role === 'broker' && !phase3Enabled && agent.id !== user.id && agent.role !== 'agent' && (
                  <div className="w-full mt-2 text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1.5 flex items-start gap-1">
                    <Bot className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>Phase 3 AI locked. Upgrade in Pricing to unlock autonomous booking.</span>
                  </div>
                )}

                {/* Delete button - super admin, franchise owner, or granted users */}
                {(user.role === 'super_admin' || user.can_delete_users) && agent.id !== user.id && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 mt-2"
                    onClick={() => deleteAgent(agent.id, agent.name)}
                    data-testid={`delete-user-${agent.id}`}
                  >
                    Delete User
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="bg-slate-50 border-slate-200">
        <CardHeader>
          <CardTitle className="text-base">Access Levels Explained</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <p className="font-semibold text-slate-900">Agent Access:</p>
            <ul className="list-disc list-inside text-slate-600 ml-2 mt-1">
              <li>Book loads and manage carriers</li>
              <li>View shippers and drivers</li>
              <li>Download PDFs and export data</li>
              <li>Cannot send emails or manage others</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-900">Broker Access:</p>
            <ul className="list-disc list-inside text-slate-600 ml-2 mt-1">
              <li>All agent permissions PLUS:</li>
              <li>Send forms via email to carriers/shippers</li>
              <li>Promote/demote agents, assign managers</li>
              <li>Delete agents and brokers within franchise</li>
              <li>Full system management</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-900">Manager Access:</p>
            <ul className="list-disc list-inside text-slate-600 ml-2 mt-1">
              <li>All broker permissions PLUS:</li>
              <li>Assigned to manage one or more franchises</li>
              <li>Oversee operations across assigned locations</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Assign Phase 3 Dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) setBulkResult(null); }}>
        <DialogContent className="max-w-2xl" data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Assign Phase 3 AI</DialogTitle>
          </DialogHeader>
          {bulkResult ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <p className="text-2xl font-bold text-green-700">{bulkResult.granted_count}</p>
                  <p className="text-xs uppercase tracking-widest text-green-600 font-semibold">Granted</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-2xl font-bold text-red-700">{bulkResult.failed_count}</p>
                  <p className="text-xs uppercase tracking-widest text-red-600 font-semibold">Failed</p>
                </div>
              </div>
              {(bulkResult.failed || []).length > 0 && (
                <div className="border border-red-200 rounded p-3 max-h-40 overflow-y-auto">
                  <p className="text-xs font-semibold text-red-700 mb-2">Failed to grant:</p>
                  {bulkResult.failed.map((f, i) => (
                    <p key={i} className="text-xs text-slate-700">
                      • <strong>{f.name || f.id}</strong> — <span className="text-red-600">{f.reason}</span>
                    </p>
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setBulkResult(null); openBulkAssign(); }}>
                  Assign More
                </Button>
                <Button onClick={() => setBulkOpen(false)} data-testid="bulk-close-btn">Done</Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                Select up to 25 Brokers/Managers to grant Phase 3 AI. Seat cap and franchise rules are enforced per user.
              </p>
              {bulkLoading ? (
                <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
              ) : bulkEligible.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">No eligible users. Promote agents to Manager first.</p>
              ) : (
                <div className="border rounded max-h-72 overflow-y-auto divide-y">
                  {bulkEligible.map((u) => (
                    <label
                      key={u.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                      data-testid={`bulk-row-${u.id}`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-amber-500"
                        checked={bulkSelected.has(u.id)}
                        onChange={() => toggleBulkSelect(u.id)}
                        data-testid={`bulk-checkbox-${u.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{u.name}</p>
                        <p className="text-xs text-slate-500">
                          {u.email} · {u.role} · AI: {u.ai_mode || 'assistant'}
                        </p>
                        {u.company_name && (
                          <p className="text-[10px] text-amber-700 font-medium mt-0.5">
                            {u.company_name}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-slate-500">
                  {bulkSelected.size} selected {bulkSelected.size > 25 ? '(max 25)' : ''}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
                  <Button
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    onClick={submitBulkAssign}
                    disabled={bulkSubmitting || bulkSelected.size === 0 || bulkSelected.size > 25}
                    data-testid="bulk-submit-btn"
                  >
                    {bulkSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Bot className="w-4 h-4 mr-1.5" />}
                    Grant Phase 3 to {bulkSelected.size}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 3 Holders Dialog */}
      <Dialog open={holdersOpen} onOpenChange={setHoldersOpen}>
        <DialogContent className="max-w-2xl" data-testid="phase3-holders-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Current Phase 3 AI Holders
            </DialogTitle>
          </DialogHeader>
          {holdersLoading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
          ) : holders.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">
              No one currently holds Phase 3 access. Use "Bulk Assign" or the assign button on a user card to grant it.
            </p>
          ) : (
            <div className="border rounded max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                    <th className="py-2 px-3">Name</th>
                    <th className="py-2 px-3">Role</th>
                    <th className="py-2 px-3">Granted By</th>
                    <th className="py-2 px-3">Granted On</th>
                    <th className="py-2 px-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {holders.map(h => (
                    <tr key={h.id} className="border-t hover:bg-amber-50/40" data-testid={`holder-row-${h.id}`}>
                      <td className="py-2 px-3">
                        <p className="font-medium text-slate-900">{h.name}</p>
                        <p className="text-[10px] text-slate-500">{h.email}</p>
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-600 capitalize">{h.role?.replace('_', ' ')}</td>
                      <td className="py-2 px-3 text-sm">
                        {h.granted_by ? (
                          <>
                            <span className="text-slate-800">{h.granted_by}</span>
                            <span className="text-[10px] text-slate-500 ml-1">({h.granted_by_role?.replace('_', ' ')})</span>
                          </>
                        ) : (
                          <span className="text-slate-400 text-xs">Unknown</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-500 whitespace-nowrap">
                        {h.granted_at ? new Date(h.granted_at).toLocaleString() : '—'}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {((user.role === 'super_admin' || user.role === 'broker') && h.id !== user.id) ? (
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 px-2 text-[11px]"
                            onClick={() => revokeHolder(h)}
                            data-testid={`revoke-holder-${h.id}`}
                          >
                            Delete
                          </Button>
                        ) : (
                          <span className="text-[10px] text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <p className="text-xs text-slate-500 mr-auto">Total: {holders.length} holder{holders.length !== 1 ? 's' : ''}</p>
            <Button
              variant="outline"
              onClick={exportHoldersCSV}
              disabled={!holders.length}
              data-testid="export-holders-csv-btn"
            >
              Export CSV
            </Button>
            <Button onClick={() => setHoldersOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Drivers Dialog — Phase 3 dispatcher roster */}
      <Dialog open={driversOpen} onOpenChange={setDriversOpen}>
        <DialogContent className="max-w-2xl" data-testid="manage-drivers-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              Manage Drivers · {driversManager?.name || ''}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Pick up to <strong>{MAX_DRIVERS_PER_P3}</strong> drivers this Phase 3 dispatcher will handle.
            Unassigned drivers or drivers already on this roster are eligible; drivers assigned elsewhere are not shown.
          </p>
          {driversLoading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
          ) : driversAvailable.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">
              No eligible drivers. Add drivers on the Drivers page first, then come back.
            </p>
          ) : (
            <div className="border rounded max-h-96 overflow-y-auto">
              {driversAvailable.map(d => {
                const chosen = driversSelected.has(d.id);
                const takenByOther = d.assigned_agent_id && d.assigned_agent_id !== driversManager?.id;
                return (
                  <label
                    key={d.id}
                    className={`flex items-center gap-3 px-4 py-3 border-b last:border-b-0 cursor-pointer transition-colors ${
                      chosen ? 'bg-blue-50' : takenByOther ? 'bg-slate-50 opacity-60 cursor-not-allowed' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`driver-row-${d.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={chosen}
                      disabled={takenByOther}
                      onChange={() => toggleDriverSelect(d.id)}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">{d.name}</p>
                      <p className="text-[11px] text-slate-500">
                        {d.truck_type || '—'} · {d.phone || 'No phone'}
                        {takenByOther && <span className="ml-2 text-red-600">Assigned to {d.assigned_agent_name}</span>}
                      </p>
                      {chosen && d.active_load ? (
                        <div className="mt-1 space-y-1" data-testid={`active-load-${d.id}`}>
                          <p className="text-[11px]">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">
                              <Truck className="w-3 h-3" />
                              #{d.active_load.load_number} · {d.active_load.pickup_city}, {d.active_load.pickup_state} → {d.active_load.delivery_city}, {d.active_load.delivery_state}
                            </span>
                          </p>
                          {/* Compact timeline: Pickup → In-Transit → Delivered */}
                          <LoadTimeline status={d.active_load.status} />
                        </div>
                      ) : chosen ? (
                        <p className="text-[11px] mt-0.5 text-slate-400 italic" data-testid={`no-load-${d.id}`}>No active load — open for a booking</p>
                      ) : null}
                      {chosen && d.ytd_dispatch_fee !== undefined && (
                        <p className="text-[10px] mt-1 text-slate-500" data-testid={`ytd-payout-${d.id}`}>
                          <span className="font-medium text-slate-700">YTD:</span>{' '}
                          <span className="font-mono text-emerald-700">${d.ytd_dispatch_fee.toLocaleString()}</span> dispatch fee
                          <span className="text-slate-400"> · {d.ytd_loads} loads · ${d.ytd_gross?.toLocaleString?.() || 0} gross</span>
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <p className="text-xs text-slate-500 mr-auto">
              Selected: <strong>{driversSelected.size}</strong> / {MAX_DRIVERS_PER_P3}
            </p>
            <Button variant="outline" onClick={() => setDriversOpen(false)} disabled={driversSaving}>Cancel</Button>
            <Button
              onClick={saveDriverAssignments}
              disabled={driversSaving || driversLoading}
              data-testid="save-driver-assignments-btn"
            >
              {driversSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Assignments'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Franchises Dialog — super_admin assigns orgs to a broker_manager */}
      <Dialog open={franchiseOpen} onOpenChange={setFranchiseOpen}>
        <DialogContent className="max-w-lg" data-testid="manage-franchises-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Manage Franchises · {franchiseTarget?.name || ''}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Pick which franchises this manager oversees. They'll appear on his dashboard's Managed Franchises panel.
          </p>
          <div className="flex gap-2 -mt-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => setFranchiseSelected(new Set(franchiseOrgs.map(o => o.id)))}
              data-testid="bulk-select-all-franchises"
            >
              Select All ({franchiseOrgs.length})
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-slate-500"
              onClick={() => setFranchiseSelected(new Set())}
              data-testid="bulk-clear-franchises"
            >
              Clear All
            </Button>
          </div>
          <div className="border rounded max-h-80 overflow-y-auto">
            {franchiseOrgs.length === 0 ? (
              <p className="p-4 text-sm text-slate-500 text-center">No franchises yet.</p>
            ) : franchiseOrgs.map(o => {
              const chosen = franchiseSelected.has(o.id);
              return (
                <label
                  key={o.id}
                  className={`flex items-center gap-3 px-4 py-3 border-b last:border-b-0 cursor-pointer transition-colors ${
                    chosen ? 'bg-indigo-50' : 'hover:bg-slate-50'
                  }`}
                  data-testid={`franchise-row-${o.id}`}
                >
                  <input
                    type="checkbox"
                    checked={chosen}
                    onChange={() => toggleFranchiseSelect(o.id)}
                    className="w-4 h-4"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{o.company_name}</p>
                    <p className="text-[11px] text-slate-500">
                      Code: {o.master_code} · {(o.plan || o.subscription_tier || 'basic').toUpperCase()} · {o.status || 'active'}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <p className="text-xs text-slate-500 mr-auto">Selected: <strong>{franchiseSelected.size}</strong></p>
            <Button variant="outline" onClick={() => setFranchiseOpen(false)} disabled={franchiseSaving}>Cancel</Button>
            <Button
              onClick={saveFranchiseAssignments}
              disabled={franchiseSaving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="save-franchise-assignments-btn"
            >
              {franchiseSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
